import { useState } from 'react';
import { Star, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// 简单的实心星形图标组件
const StarFilled = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWatchlistSummary, invalidateWatchlistSummaryCache } from '@/hooks/useWatchlistSummary';
import { watchlistApi } from '@/api/api';
import { WatchlistHeader } from './WatchlistHeader';
import { WatchlistItem } from './WatchlistItem';
import { WatchlistFooter } from './WatchlistFooter';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { toast } from 'sonner';
import type { EntityType } from '@/types/watchlist';
import { ENTITY_TYPE_ORDER, ENTITY_TYPE_I18N_KEY } from './watchlistViews';

/**
 * Watchlist 下拉菜单主组件
 *
 * 重构后(redesign-watchlist-via-views OpenSpec change):
 *   - 数据源走 useWatchlistSummary (POST /pms/user-entity-links/search +
 *     summaryType=watchlist),拿后端 GROUP BY entity_type 的扁平计数
 *   - 不再按 linkDescription 二级分组 —— 简化为单层 entityType 列表
 *   - WatchButton 不再选 group,单击 toggle
 *   - 旧 group 编辑相关组件 (GroupSelectDialog / GroupHeader / RenameGroupDialog)
 *     不再被这里 import
 */
export function WatchlistDropdown() {
  const { t } = useTranslation('chrome');
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type?: EntityType;
    isClearAll?: boolean;
  } | null>(null);

  // 始终加载 summary —— 右上角星星的黄/灰和总数本身就要靠这份数据驱动,
  // 不能等用户点开 dropdown 才取。模块级 cache(5 分钟 TTL)+ in-flight 去重
  // 保证跨页面/跨组件只发一次,切 View 不重复打。
  const { data: summary, loading } = useWatchlistSummary();

  const totalCount = summary?.totalCount ?? 0;
  const byEntityType = summary?.byEntityType ?? {};

  // 按业务顺序枚举,过滤掉没有数据的类型
  const entries = ENTITY_TYPE_ORDER
    .map((type) => ({ type, count: byEntityType[type] ?? 0 }))
    .filter((entry) => entry.count > 0);

  // 处理删除单个类型
  const handleDeleteType = (entityType: EntityType) => {
    setDeleteTarget({ type: entityType });
    setDeleteModalOpen(true);
  };

  // 处理清空所有
  const handleClearAll = () => {
    setDeleteTarget({ isClearAll: true });
    setDeleteModalOpen(true);
  };

  // 确认删除
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.isClearAll) {
        await watchlistApi.clearAllWatches();
        invalidateWatchlistSummaryCache();
        toast.success(t('watchlist.toastCleared'));
      } else if (deleteTarget.type) {
        await watchlistApi.deleteMyWatchesByType(deleteTarget.type);
        invalidateWatchlistSummaryCache();
        toast.success(t('watchlist.toastRemovedType', {
          type: t(`watchlist.entityType.${ENTITY_TYPE_I18N_KEY[deleteTarget.type]}`),
        }));
      }
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting watchlist items:', error);
      toast.error(t('watchlist.toastDeleteFailed'));
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="relative p-2 rounded-md hover:bg-accent transition-colors"
            aria-label={t('watchlist.triggerAriaLabel')}
          >
            {totalCount > 0 ? (
              <StarFilled className="h-5 w-5 text-amber-400" />
            ) : (
              <Star className="h-5 w-5 text-slate-300" />
            )}
            {totalCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center bg-amber-500 text-white text-[10px] font-bold leading-none rounded-full ring-2 ring-background px-1">
                {totalCount > 99 ? '99+' : totalCount}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 flex flex-col max-h-[600px]" align="end">
          <WatchlistHeader totalCount={totalCount} />

          {loading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t('watchlist.loading')}
            </div>
          ) : entries.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-full bg-muted p-3">
                  <BookOpen className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{t('watchlist.emptyTitle')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('watchlist.emptyHint')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <ScrollArea className="flex-1 min-h-0">
              <div className="py-2">
                {entries.map(({ type, count }) => (
                  <WatchlistItem
                    key={type}
                    entityType={type}
                    count={count}
                    onDelete={() => handleDeleteType(type)}
                    onNavigate={() => setOpen(false)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}

          {entries.length > 0 && (
            <WatchlistFooter onClearAll={handleClearAll} disabled={loading} />
          )}
        </PopoverContent>
      </Popover>

      <ConfirmDeleteModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        entityType={deleteTarget?.type}
        count={deleteTarget?.type ? byEntityType[deleteTarget.type] : undefined}
        isClearAll={deleteTarget?.isClearAll}
      />
    </>
  );
}
